import { VISUAL_REVEAL_RADIUS_METERS } from '@repo/map-preferences/fog-tiles';

export class FogSpatialIndex {
    private cells = new Map<string, Array<[number, number]>>();

    constructor(private readonly cellSize: number) {}

    private cellCoordKey(cx: number, cy: number): string {
        return `${cx},${cy}`;
    }

    private cellKey(mx: number, my: number): string {
        return this.cellCoordKey(Math.floor(mx / this.cellSize), Math.floor(my / this.cellSize));
    }

    private addCircle(circle: [number, number]): void {
        const key = this.cellKey(circle[0], circle[1]);
        let bucket = this.cells.get(key);
        if (!bucket) {
            bucket = [];
            this.cells.set(key, bucket);
        }
        bucket.push(circle);
    }

    addCircles(circles: Array<[number, number]>): void {
        for (const circle of circles) {
            this.addCircle(circle);
        }
    }

    getNovelCircles(circles: Array<[number, number]>, radiusSq: number): Array<[number, number]> {
        const batchIndex = new FogSpatialIndex(this.cellSize);
        const novel: Array<[number, number]> = [];

        for (const circle of circles) {
            if (this.hasNeighborWithin(circle[0], circle[1], radiusSq)) continue;
            if (batchIndex.hasNeighborWithin(circle[0], circle[1], radiusSq)) continue;

            batchIndex.addCircle(circle);
            novel.push(circle);
        }

        return novel;
    }

    /**
     * Returns true if any indexed circle center is within `radiusSq` of (mx, my).
     * Checks the 3×3 cell neighborhood around the query point.
     * Precondition: sqrt(radiusSq) <= cellSize — larger radii may miss circles
     * in cells beyond the immediate neighborhood.
     */
    hasNeighborWithin(mx: number, my: number, radiusSq: number): boolean {
        const cx = Math.floor(mx / this.cellSize);
        const cy = Math.floor(my / this.cellSize);
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                const bucket = this.cells.get(this.cellCoordKey(cx + dx, cy + dy));
                if (!bucket) continue;
                for (const [bx, by] of bucket) {
                    const ddx = mx - bx;
                    const ddy = my - by;
                    if (ddx * ddx + ddy * ddy <= radiusSq) return true;
                }
            }
        }
        return false;
    }

    rebuild(circles: Array<[number, number]>): void {
        this.cells.clear();
        this.addCircles(circles);
    }

    clear(): void {
        this.cells.clear();
    }
}

// Sized to the max caller search radius (pin filter, 1.1×) so the 3×3 neighborhood always covers it.
export const fogSpatialIndex = new FogSpatialIndex(VISUAL_REVEAL_RADIUS_METERS * 1.1);
